import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  FaUsers,
  FaKeyboard,
  FaClipboardList,
  FaTrophy,
  FaClock,
  FaChartLine
} from 'react-icons/fa'
import { BiTargetLock } from 'react-icons/bi'
import { MdSpellcheck } from 'react-icons/md'
import { toast } from 'react-toastify'
import { sendApiRequest } from '../utils/api';

const Overview = () => {
  const [stats, setStats] = useState([
    {
      title: 'Total Students',
      value: '0',
      icon: <FaUsers className="w-8 h-8 text-sky-700" />,
      trend: '0%',
      bgColor: 'bg-sky-100'
    },
    {
      title: 'Active Students',
      value: '0',
      icon: <FaUsers className="w-8 h-8 text-emerald-600" />,
      trend: '0%',
      bgColor: 'bg-emerald-100'
    },
    {
      title: 'Total Tests',
      value: '0',
      icon: <FaKeyboard className="w-8 h-8 text-orange-600" />,
      trend: '+0%',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'Active Tests',
      value: '0',
      icon: <FaClock className="w-8 h-8 text-green-600" />,
      trend: '+0%',
      bgColor: 'bg-green-100'
    }
  ])

  const [performanceStats, setPerformanceStats] = useState([
    {
      title: 'Top Speed',
      value: '0 WPM',
      icon: <FaChartLine className="w-6 h-6 text-blue-500" />
    },
    {
      title: 'Avg. WPM',
      value: '0 WPM',
      icon: <FaKeyboard className="w-6 h-6 text-orange-500" />
    },
    {
      title: 'Avg. Accuracy',
      value: '0%',
      icon: <BiTargetLock className="w-6 h-6 text-purple-500" />
    },
    {
      title: 'Certified Users',
      value: '0',
      icon: <FaTrophy className="w-6 h-6 text-yellow-500" />
    }
  ])

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchStats = async () => {
      try {
        const response = await sendApiRequest('api/admin/stats', {}, 'GET');
        console.log(response)
        
        // Update stats
        setStats([
          {
            title: 'Total Students',
            value: response.totalStudents.toLocaleString(),
            icon: <FaUsers className="w-8 h-8 text-sky-700" />,
            trend: `+${response.studentGrowthRate}%`,
            bgColor: 'bg-sky-100'
          },
          {
            title: 'Active Students',
            value: response.activeStudents.toLocaleString(),
            icon: <FaUsers className="w-8 h-8 text-emerald-600" />,
            trend: `+${response.studentGrowthRate}%`,
            bgColor: 'bg-emerald-100'
          },
          {
            title: 'Total Tests',
            value: response.testResults.total.toLocaleString(),
            icon: <FaKeyboard className="w-8 h-8 text-orange-600" />,
            trend: `+${response.testGrowthRate}%`,
            bgColor: 'bg-orange-100'
          },
          {
            title: 'Active Tests',
            value: response.testResults.active.toLocaleString(),
            icon: <FaClock className="w-8 h-8 text-green-600" />,
            trend: `+${response.testGrowthRate}%`,
            bgColor: 'bg-green-100'
          }
        ]);

        // Update performance stats
        setPerformanceStats([
          {
            title: 'Top Speed',
            value: `${response.topSpeed} WPM`,
            icon: <FaChartLine className="w-6 h-6 text-blue-500" />
          },
          {
            title: 'Avg. WPM',
            value: `${response.avgWPM} WPM`,
            icon: <FaKeyboard className="w-6 h-6 text-orange-500" />
          },
          {
            title: 'Avg. Accuracy',
            value: `${response.avgAccuracy}%`,
            icon: <BiTargetLock className="w-6 h-6 text-purple-500" />
          },
          {
            title: 'Certified Users',
            value: response.certifiedUsers.toLocaleString(),
            icon: <FaTrophy className="w-6 h-6 text-yellow-500" />
          }
        ]);

      } catch (error) {
        console.error('Error fetching stats:', error);
        toast.error('Failed to fetch platform statistics');
      }
    };

    fetchStats();
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto bg-gradient-to-br from-white to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-10 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
            Welcome to Master Stenography
          </h1>
          <p className="text-sm sm:text-base text-gray-500">
            Monitor your platform's performance and student progress
          </p>
        </div>
        <div className="text-gray-700 bg-white px-6 py-3 rounded-xl shadow border border-gray-200 font-medium">
          {new Date().toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            weekday: 'long'
          })}
        </div>
      </div>

      {/* Overview Banner */}
      <div className="bg-gradient-to-r from-blue-200 via-sky-100 to-white rounded-2xl p-6 sm:p-8 mb-8 sm:mb-12 border border-blue-100 relative overflow-hidden">
        <div className="relative z-10 max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">Platform Statistics</h2>
          <p className="mb-4 sm:mb-6 text-sm sm:text-base text-gray-600 leading-relaxed">
            Your typing platform is showing excellent progress! Students are achieving higher WPM scores
            and maintaining impressive accuracy rates.
          </p>
          {/* <button className="bg-sky-600 text-white px-6 py-2 rounded-lg hover:bg-sky-700 transition">
            View Detailed Reports
          </button> */}
        </div>
        <div className="absolute right-0 top-0 h-full w-1/2 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/white-diamond.png')] bg-cover"></div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`${stat.bgColor} p-4 sm:p-6 rounded-xl shadow hover:shadow-lg transform transition-all duration-300 hover:-translate-y-1 border border-gray-200`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 sm:p-3 rounded-lg bg-white shadow">{stat.icon}</div>
              <span className="text-sm font-semibold text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200">
                {stat.trend}
              </span>
            </div>
            <h3 className="text-xs sm:text-sm text-gray-500 mb-1">{stat.title}</h3>
            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Performance Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {performanceStats.map((stat, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded-xl shadow hover:shadow-md transition-all border border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gray-100">
                {stat.icon}
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.title}</p>
                <p className="text-lg font-semibold text-gray-800">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Overview
